import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, authAPI } from '../api/auth';
import { tenantsAPI } from '../api/tenants';
import { setApiBaseUrl } from '../api/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, tenantId?: number) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  /** Admin or PM: full tenant task/project visibility and may edit submitted reports / approve / reject. */
  isStaffReviewer: () => boolean;
  isTechnician: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        // Verify token and switch to tenant's backend if they use local folder/PDFs
        authAPI.getMe()
          .then((currentUser) => {
            setUser(currentUser);
            localStorage.setItem('user', JSON.stringify(currentUser));
            return tenantsAPI.getMe();
          })
          .then((tenant) => {
            if (tenant?.apiBaseUrl) setApiBaseUrl(tenant.apiBaseUrl);
          })
          .catch(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setApiBaseUrl(null);
          })
          .finally(() => setLoading(false));
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string, tenantId?: number) => {
    const response = await authAPI.login(email, password, tenantId);
    // Merge tenant into user so user.tenantId is set (needed for tenant-scoped folder, etc.)
    const mergedUser = {
      ...response.user,
      tenantId: response.tenant?.tenantId ?? response.user.tenantId,
      tenantName: response.tenant?.tenantName ?? response.user.tenantName,
      tenantSubdomain: response.tenant?.tenantSubdomain ?? response.user.tenantSubdomain,
      apiBaseUrl: response.tenant?.apiBaseUrl ?? response.user.apiBaseUrl,
    };
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(mergedUser));
    setUser(mergedUser);
    // If tenant has their own backend (local folder/PDFs), use it for all API calls
    try {
      const tenant = await tenantsAPI.getMe();
      if (tenant?.apiBaseUrl) setApiBaseUrl(tenant.apiBaseUrl);
      else setApiBaseUrl(null);
    } catch {
      setApiBaseUrl(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setApiBaseUrl(null);
  };

  const isAdmin = () => user?.role === 'ADMIN';
  const isStaffReviewer = () => user?.role === 'ADMIN' || user?.role === 'PM';
  const isTechnician = () => user?.role === 'TECHNICIAN';

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isStaffReviewer, isTechnician }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

